<template>
  <div class="supplier-form">
    <form @submit.prevent="handleSubmit">
      <!-- Basic Info Section -->
      <div class="form-section">
        <h4>Basisgegevens</h4>

        <TextInput
          id="supplier_name"
          label="Naam"
          v-model="form.name"
          placeholder="Bijv. Michel Rolluiken"
          :error="errors.name"
          :rules="['required', 'maxLength:255']"
        />

        <div class="form-row">
          <TextInput
            id="supplier_contact"
            label="Contactpersoon"
            v-model="form.contact_person"
            placeholder="Jan Jansen"
          />
          <TextInput
            id="supplier_email"
            label="E-mail"
            v-model="form.email"
            placeholder="orders@leverancier.nl"
            type="email"
          />
        </div>

        <div class="form-row">
          <TextInput
            id="supplier_phone"
            label="Telefoon"
            v-model="form.phone"
            placeholder="+31 6 12345678"
          />
          <TextInput
            id="supplier_website"
            label="Website"
            v-model="form.website"
            placeholder="https://www.leverancier.nl"
          />
        </div>
      </div>

      <!-- Export Settings Section -->
      <div class="form-section">
        <h4>Export Instellingen</h4>

        <SelectInput
          id="supplier_export_method"
          label="Export Methode"
          v-model="form.export_method"
          :options="exportMethodOptions"
          placeholder="Selecteer een methode"
        />

        <!-- Webhook Settings -->
        <template v-if="form.export_method === 'webhook'">
          <TextInput
            id="supplier_webhook_url"
            label="Webhook URL"
            v-model="form.webhook_url"
            placeholder="https://hook.eu1.make.com/..."
          />
          <TextInput
            id="supplier_webhook_secret"
            label="Webhook Secret (optioneel)"
            v-model="form.webhook_secret"
            placeholder="Geheime sleutel voor ondertekening"
            type="password"
          />
        </template>

        <!-- Email / Template: order destination (handlers email the export) -->
        <template v-if="form.export_method === 'email' || form.export_method === 'template'">
          <TextInput
            id="supplier_order_email"
            label="Order E-mail"
            v-model="form.order_email"
            placeholder="orders@leverancier.nl"
            type="email"
          />
          <TextInput
            id="supplier_email_subject"
            label="E-mail Onderwerp Template"
            v-model="form.email_subject_template"
            placeholder="Order {{order_number}} - {{customer_name}}"
          />
          <TextAreaInput
            v-if="form.export_method === 'email'"
            id="supplier_email_body"
            label="E-mail Body Template"
            v-model="form.email_body_template"
            placeholder="Beste leverancier,&#10;&#10;Hierbij een nieuwe order:&#10;{{order_details}}"
            :rows="4"
          />
        </template>

        <!-- Template Info -->
        <template v-if="form.export_method === 'template'">
          <div class="template-info">
            <p class="template-info__text">
              Het bestelformat voor deze leverancier wordt beheerd via de API.
              Configureer de product-mapping per product via de "Leverancier" tab op de productpagina.
            </p>
            <StatusBadge
              v-if="hasTemplate"
              variant="success"
              size="small"
            >
              Template geconfigureerd
            </StatusBadge>
            <StatusBadge
              v-else
              variant="draft"
              size="small"
            >
              Nog geen template
            </StatusBadge>
          </div>
        </template>
      </div>

      <!-- Delivery Settings Section -->
      <div class="form-section">
        <h4>Levering</h4>

        <div class="form-row">
          <NumberInput
            id="supplier_delivery_days"
            label="Standaard levertijd (dagen)"
            v-model="form.default_delivery_days"
            :min="1"
            :max="365"
            placeholder="14"
          />
          <div class="form-field">
            <CheckboxInput
              id="supplier_requires_confirmation"
              label="Bevestiging vereist"
              v-model="form.requires_confirmation"
            />
            <small class="form-hint">Leverancier moet order bevestigen</small>
          </div>
        </div>
      </div>

      <!-- Notes Section -->
      <div class="form-section">
        <h4>Notities</h4>
        <TextAreaInput
          id="supplier_notes"
          label="Interne notities"
          v-model="form.notes"
          placeholder="Notities over deze leverancier..."
          :rows="3"
        />
      </div>

      <!-- Active Toggle -->
      <div class="form-section form-section--toggle">
        <CheckboxInput id="supplier_active" label="Leverancier actief" v-model="form.active" />
      </div>

      <!-- Actions -->
      <div class="supplier-form__actions">
        <Btn
          v-if="isEdit"
          type="button"
          variant="danger-outline"
          @click="handleDelete"
          icon="trash"
        >
          Verwijderen
        </Btn>
        <div class="supplier-form__actions-right">
          <Btn type="submit" :text="isEdit ? 'Opslaan' : 'Toevoegen'" variant="secondary" />
        </div>
      </div>
    </form>
  </div>
</template>

<script>
  import { defineComponent, ref, computed, watch, onMounted } from 'vue';
  import { supplierService } from '@/services/supplierService';

  import Btn from '@/components/partials/Btn.vue';
  import StatusBadge from '@/components/partials/StatusBadge.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import CheckboxInput from '@/components/inputs/CheckboxInput.vue';

  export default defineComponent({
    name: 'CreateEditSupplierModal',

    components: {
      Btn,
      StatusBadge,
      TextInput,
      TextAreaInput,
      SelectInput,
      NumberInput,
      CheckboxInput,
    },

    props: {
      /**
       * Existing supplier data (null for create mode)
       */
      supplier: {
        type: Object,
        default: null,
      },
      /**
       * Callback when form is submitted successfully
       */
      onSubmit: {
        type: Function,
        required: true,
      },
      /**
       * Callback when supplier should be deleted
       */
      onDelete: {
        type: Function,
        default: null,
      },
      /**
       * Callback when form has errors
       */
      onError: {
        type: Function,
        default: () => {},
      },
    },

    setup(props) {
      const errors = ref({});

      const form = ref({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        website: '',
        export_method: 'webhook',
        webhook_url: '',
        webhook_secret: '',
        order_email: '',
        email_subject_template: 'Order {{order_number}}',
        email_body_template: '',
        default_delivery_days: 14,
        requires_confirmation: false,
        notes: '',
        active: true,
      });

      const isEdit = computed(() => !!props.supplier);
      const hasTemplate = ref(false);

      const checkTemplate = async () => {
        if (!props.supplier?.id) return;
        try {
          const template = await supplierService.getSupplierTemplate(props.supplier.id);
          hasTemplate.value = !!template?.format_type;
        } catch {
          hasTemplate.value = false;
        }
      };

      onMounted(() => {
        if (isEdit.value) checkTemplate();
      });

      const exportMethodOptions = [
        { value: 'webhook', label: 'Webhook (Make/Zapier)' },
        { value: 'email', label: 'E-mail' },
        { value: 'template', label: 'Custom Template (geavanceerd)' },
        { value: 'manual', label: 'Handmatig (download)' },
      ];

      // Initialize form with existing supplier data
      watch(
        () => props.supplier,
        (supplier) => {
          if (supplier) {
            form.value = {
              name: supplier.name || '',
              contact_person: supplier.contact_person || '',
              email: supplier.email || '',
              phone: supplier.phone || '',
              website: supplier.website || '',
              export_method: supplier.export_method || 'webhook',
              webhook_url: supplier.webhook_url || '',
              webhook_secret: supplier.webhook_secret || '',
              order_email: supplier.order_email || '',
              email_subject_template: supplier.email_subject_template || 'Order {{order_number}}',
              email_body_template: supplier.email_body_template || '',
              default_delivery_days: supplier.default_delivery_days || 14,
              requires_confirmation: supplier.requires_confirmation || false,
              notes: supplier.notes || '',
              active: supplier.active !== false,
            };
          }
        },
        { immediate: true }
      );

      const validate = () => {
        errors.value = {};

        if (!form.value.name?.trim()) {
          errors.value.name = 'Naam is verplicht';
        }

        return Object.keys(errors.value).length === 0;
      };

      const handleSubmit = () => {
        if (!validate()) {
          props.onError(errors.value);
          return;
        }

        props.onSubmit(form.value);
      };

      const handleDelete = () => {
        if (props.onDelete) {
          props.onDelete();
        }
      };

      return {
        form,
        errors,
        isEdit,
        hasTemplate,
        exportMethodOptions,
        handleSubmit,
        handleDelete,
      };
    },
  });
</script>

<style scoped lang="scss">
  .supplier-form {
    &__actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 1.5rem;
      padding-top: 1rem;
      border-top: 1px solid var(--slate-200);
    }

    &__actions-right {
      display: flex;
      gap: 0.75rem;
      margin-left: auto;
    }
  }

  .form-section {
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--slate-100);

    &:last-of-type {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    &--toggle {
      padding: 1rem;
      background: var(--slate-50);
      border-radius: 0.5rem;
      border-bottom: none;
    }

    h4 {
      margin: 0 0 1rem 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-700);
    }
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .form-hint {
    font-size: 0.75rem;
    color: var(--slate-500);
  }

  .template-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: var(--slate-50);
    border-radius: 0.5rem;
    border: 1px solid var(--slate-200);

    &__text {
      flex: 1;
      font-size: 0.8125rem;
      color: var(--slate-600);
      margin: 0;
    }
  }
</style>
